import { HTTP_OPTIONS, PRIVILEGED_ACCOUNTS_STATUS } from '@silverfort-nodelibs/common/utils/consts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { httpGet } from '../../../components/utils';
import { useTowerLayout } from '../../../TowerLayout/TowerLayoutProvider';
import {
    Alert,
    Badge,
    Checkbox,
    EmptyStateIcons,
    Icons,
    Table,
    Tabs,
    Tooltip,
    FloatingSnackbar,
    Button,
    ButtonStyles,
    showAlert
} from '@silverfort/sf-components';

import {
    buildExpandedTableColumns,
    buildFiltersQuery,
    buildPolicyAlertPopup,
    buildTableColumns,
    buildTableData,
    buildTabs
} from '../utils/PrivilegedUsersBuilders';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import PrivilegedAccountsInsights from '../containers/PrivilegedAccountsInsights';
import PrivilegedAccountsFilters from '../containers/PrivilegedAccountsFIlters';
import PaPolicies from '../containers/PaPolicies';
import TopBanner from '../components/TopBanner';
import { usePrivilegedAccountContext } from '../context/PrivilegedAccountsContext';
import { PricingModules, usePricingModulesState } from '../../../context/contexts/PricingModuleContext';
import { Pages, Roles } from '../../../context/contexts/UserContext/permissionsTypes';
import useHasPermissions from '../../../hooks/useHasPermissions';
import InvestigationDrawer from '../../../components/InvestigationDrawer/InvestigationDrawer';
import { useAppDrawer } from '../../../components/AppDrawer/AppDrawerProvider';
import { BulkAction } from '../types/PrivilegedAccountsTypes';
import BulkJit from '../containers/BulkJit';
import BulkFencing from '../containers/BulkFencing';

const PrivilegedUsers = () => {
    const { updatePoliciesStatus, jitConfig } = usePrivilegedAccountContext();

    const isMountedRef = useRef(true);
    const [showLoader, setShowLoader] = useState(true);
    const [showAuthLoader, setShowAuthLoader] = useState({});
    const [showPageLoader, setShowPageLoader] = useState();
    const [selectedFilters, setSelectedFilters] = useState({ hideSA: true });
    const [selectedTab, setSelectedTab] = useState(0);
    const [privilegedAccounts, setPrivilegedAccounts] = useState([]);
    const [accountsAuthentications, setAccountsAuthentications] = useState({});
    const [accountsPageNumber, setAccountsPageNumber] = useState(0);
    const [lastPageReceived, setLastPageReceived] = useState();
    const [sortSettings, setSortSettings] = useState({ orderBy: 'name', sortDirection: 'asc' });
    const [authSortSettings, setAuthSortSettings] = useState({});
    const [statistics, setStatistics] = useState();
    const [tiersCounters, setTiersCounters] = useState({});
    const [authChangeTarget, setAuthChangeTarget] = useState();
    const [highlightedUser, setHighlightedUser] = useState('');
    const [openedAccountProtectionGuid, setOpenedAccountProtectionGuid] = useState('');
    const [bulkAction, setBulkAction] = useState(null);
    const [showTopBanner, setShowTopBanner] = useState(false);
    const [abortSignal, setAbortSignal] = useState();
    const [guidsOfJitPolicyInActivationProcess, setGuidsOfJitPolicyInActivationProcess] = useState([]);
    const [isDemoMode, setIsDemoMode] = useState(false);
    const { pricingModulesState } = usePricingModulesState();
    const [onPremNHIEnabled, setOnPremNHIEnabled] = useState(false);
    const [selectedGuids, setSelectedGuids] = useState([]);
    const [allSilverfortAccountsGuids, setAllSilverfortAccountsGuids] = useState([]);
    const { displayContent } = useAppDrawer();

    const isEditor = useHasPermissions({ permission: Pages.PRIVILEGED_ACCOUNTS, role: Roles.EDITOR });

    useEffect(() => {
        const fetchAllSilverfortAccountsGuids = async () => {
            const res = await httpGet('/privilegedAccounts/allSilverfortAccountsGuids');
            setAllSilverfortAccountsGuids(res.data || []);
        };
        fetchAllSilverfortAccountsGuids();
    }, []);

    const onSelectPas = useCallback(
        (guidsToSelect) => {
            setSelectedGuids(guidsToSelect.filter((guid) => !allSilverfortAccountsGuids.includes(guid)));
        },
        [allSilverfortAccountsGuids]
    );

    const clearSelectedGuids = useCallback(() => {
        onSelectPas([]);
    }, [onSelectPas]);

    useEffect(() => {
        clearSelectedGuids();
    }, [selectedTab, clearSelectedGuids]);

    useEffect(() => {
        const fetchDemoMode = async () => {
            try {
                const res = await httpGet('/privilegedAccounts/isDemoMode');
                setIsDemoMode(res.data.pasDemoMode);
            } catch (e) {
                console.error('failed to fetch demo mode status: ', e);
            }
        };
        fetchDemoMode();
    }, []);

    useEffect(() => {
        setOnPremNHIEnabled(pricingModulesState?.[PricingModules.NHI_VISIBILITY].onPremEnabled);
    }, [pricingModulesState]);

    useEffect(() => {
        setSelectedFilters((selectedFilters) => {
            return { ...selectedFilters, hideSA: onPremNHIEnabled };
        });
    }, [onPremNHIEnabled]);

    const fetchAccountPage = async (resetData) => {
        const pageNumber = resetData ? 0 : accountsPageNumber;
        let isLastPage = resetData ? false : lastPageReceived;

        try {
            if (!resetData && privilegedAccounts.length >= tiersCounters[`tier_${selectedTab}`]) {
                console.log(`No more accounts to fetch in tier ${selectedTab}, last page: ${accountsPageNumber}`);
                isLastPage = true;
                return;
            }

            if (abortSignal) {
                console.log('cancel!');
                abortSignal.abort();
            }

            const abortController = new AbortController();
            setAbortSignal(abortController);

            const filtersString = buildFiltersQuery(selectedFilters);
            const newPageRes = await httpGet(
                `/privilegedAccounts/page/${pageNumber}?tier=${selectedTab}&orderBy=${sortSettings.orderBy}&sortDirection=${sortSettings.sortDirection}&filters=${filtersString}`,
                {
                    [HTTP_OPTIONS.REQ_OPTIONS]: {
                        signal: abortController.signal
                    }
                }
            );

            if (!isMountedRef.current) {
                return;
            }

            setAbortSignal(null);

            const allWaitingGuids = newPageRes.data
                .filter((account) => account.status > 1)
                .map((account) => account.guid);
            if (allWaitingGuids.length > 0) {
                setGuidsOfJitPolicyInActivationProcess((prev) => [...prev, ...allWaitingGuids]);
            }

            if (newPageRes.data) {
                if (resetData) {
                    setPrivilegedAccounts(newPageRes.data);
                } else {
                    setPrivilegedAccounts([...privilegedAccounts, ...newPageRes.data]);
                }
            }
        } catch (e) {
            console.error(`failed to fetch privileged accounts page number ${accountsPageNumber}: `, e);
        } finally {
            if (isMountedRef.current) {
                setLastPageReceived(isLastPage);
                setAccountsPageNumber(pageNumber);
                setShowPageLoader(false);
            }
        }
    };

    const fetchAuthentications = async () => {
        const guid = authChangeTarget;
        const targetSort = authSortSettings[guid] || { orderBy: 'count', sortDirection: 'desc' };

        if (!guid) {
            return;
        }

        try {
            const res = await httpGet(
                `/privilegedAccounts/account/${guid}?orderBy=${targetSort.orderBy}&sortDirection=${targetSort.sortDirection}`
            );

            if (!isMountedRef.current) {
                return;
            }

            setAccountsAuthentications({ ...accountsAuthentications, [guid]: res.data });
            setShowAuthLoader({ ...showAuthLoader, [guid]: false });
        } catch (e) {
            console.error(`failed to fetch account authentications for account ${guid}: `, e);
        }
    };

    const getCurrentStatusOnAccountsAfterAction = useCallback(async () => {
        if (guidsOfJitPolicyInActivationProcess.length === 0) {
            return;
        }

        const { data: newStatuses } = await httpGet(`/privilegedAccounts/accountsStatuses/`, {
            [HTTP_OPTIONS.REQ_OPTIONS]: {
                params: {
                    guids: guidsOfJitPolicyInActivationProcess
                }
            }
        });

        setPrivilegedAccounts((prevPriviledgeAccount) => {
            const updated = prevPriviledgeAccount.map((account) => {
                if (account.guid in newStatuses && newStatuses[account.guid] !== account.status) {
                    setGuidsOfJitPolicyInActivationProcess((prev) => prev.filter((guid) => guid !== account.guid));
                    return { ...account, status: newStatuses[account.guid] };
                }
                return { ...account };
            });
            return [...updated];
        });
    }, [guidsOfJitPolicyInActivationProcess]);

    const fetchStatsData = useCallback(async () => {
        try {
            const statsRes = await httpGet(`/privilegedAccounts/statistics`);
            isMountedRef.current && setStatistics(statsRes.data);
        } catch (e) {
            console.error('failed to load privileged accounts statistics.', e);
        } finally {
            isMountedRef.current && setShowLoader(false);
        }
    }, []);

    useEffect(() => {
        const intervalId = setInterval(getCurrentStatusOnAccountsAfterAction, 10000);
        return () => clearInterval(intervalId);
    }, [getCurrentStatusOnAccountsAfterAction]);

    // init effect
    useEffect(() => {
        fetchStatsData();
        return () => {
            isMountedRef.current = false;
        };
    }, [fetchStatsData]);

    // account page change effect
    useEffect(() => {
        if (!showPageLoader) {
            setShowPageLoader(true);
            accountsPageNumber && fetchAccountPage();
        }
    }, [accountsPageNumber]);

    // account sort/tab/filter change effect
    useEffect(() => {
        !showPageLoader && setShowPageLoader(true);
        fetchAccountPage(true);
    }, [sortSettings, selectedFilters, selectedTab]);

    // auth sort change effect
    useEffect(() => {
        setShowAuthLoader({ ...showAuthLoader, [authChangeTarget]: true });
        fetchAuthentications();
    }, [authSortSettings]);

    // tier counters recalculation effect
    useEffect(() => {
        const fetchTiersCounters = async () => {
            const filtersString = buildFiltersQuery(selectedFilters);
            const countersRes = await httpGet(`/privilegedAccounts/counters?filters=${filtersString}`);
            isMountedRef.current && setTiersCounters(countersRes.data);
        };

        fetchTiersCounters();
    }, [selectedFilters]);

    const handleOnExpand = async (guid) => {
        if (!accountsAuthentications[guid]) {
            setShowAuthLoader({ ...showAuthLoader, [guid]: true });
            const res = await httpGet(`/privilegedAccounts/account/${guid}`);

            if (!isMountedRef.current) {
                return;
            }
            setAccountsAuthentications({
                ...accountsAuthentications,
                [guid]: res.data
            });

            setShowAuthLoader({ ...showAuthLoader, [guid]: false });
        }
    };

    const handleProtectionSettings = (item) => {
        if (item.is_silverfort_account) {
            return;
        }
        setOpenedAccountProtectionGuid(item.guid);
        setHighlightedUser(item.guid);
    };

    const handleInvestigationClick = (guid, identifier) => {
        displayContent(<InvestigationDrawer guid={guid} identifier={identifier} adType={AD_ENTITY_TYPES.USER} />);
    };

    const tableColumns = useMemo(
        () =>
            buildTableColumns(
                selectedTab,
                tiersCounters[`tier_${selectedTab}`],
                (guid, is_silverfort_account) => handleProtectionSettings({ guid, is_silverfort_account }),
                handleInvestigationClick
            ),
        [selectedTab, tiersCounters]
    );

    const expandedTableColumns = useMemo(() => buildExpandedTableColumns(), []);

    const tableData = useMemo(
        () =>
            buildTableData(
                privilegedAccounts,
                accountsAuthentications,
                expandedTableColumns,
                showAuthLoader,
                authSortSettings,
                setAuthSortSettings,
                setAuthChangeTarget,
                !!selectedFilters.crossTier
            ),
        [showPageLoader, showAuthLoader, privilegedAccounts, accountsAuthentications, !!selectedFilters.crossTier]
    );

    const tabs = useMemo(() => buildTabs(tiersCounters), [tiersCounters]);

    const onSearchChange = (val) => {
        setSelectedFilters({ ...selectedFilters, name: val });
    };

    const onHideSaChange = () => {
        setSelectedFilters((selectedFilters) => {
            return { ...selectedFilters, hideSA: !selectedFilters.hideSA };
        });
    };

    const { setLayoutContent } = useTowerLayout();
    useEffect(() => {
        if (!isDemoMode) {
            return;
        }
        const layoutData = (
            <div className="flex">
                <Tooltip
                    width={320}
                    title={'Demo Mode'}
                    description={
                        'While in Demo Mode, JIT duration and tier calculation time do not reflect the defined configuration'
                    }
                    placement="bottom-start">
                    <Badge text={'Demo Mode'} color={'primary'} icon={'AlarmClock'} />
                </Tooltip>
            </div>
        );
        setLayoutContent(layoutData);
        return () => setLayoutContent('');
    }, [isDemoMode, setLayoutContent]);

    const isBulkJitPossible = useMemo(() => {
        const isJitConfigured = jitConfig?.isJitConfigSet;
        const isBreakGlassSelected = selectedGuids.some((guid) => jitConfig?.jitBreakGlassGuids.includes(guid));
        return isJitConfigured && !isBreakGlassSelected;
    }, [jitConfig?.isJitConfigSet, jitConfig?.jitBreakGlassGuids, selectedGuids]);

    const isBulkOverrides = useMemo(() => {
        return (
            (bulkAction === BulkAction.JIT &&
                privilegedAccounts.filter((account) => selectedGuids.includes(account.guid) && account.jitEnabled)
                    .length > 0) ||
            (bulkAction === BulkAction.FENCING &&
                privilegedAccounts.filter((account) => selectedGuids.includes(account.guid) && account.fencingEnabled)
                    .length > 0)
        );
    }, [selectedGuids, privilegedAccounts, bulkAction]);

    const snackbarActions = useMemo(() => {
        const actions = [
            {
                label: 'Virtual Fencing Policy',
                action: () => {
                    setBulkAction(BulkAction.FENCING);
                }
            }
        ];
        isBulkJitPossible &&
            actions.push({
                label: 'JIT Policy',
                action: () => {
                    setBulkAction(BulkAction.JIT);
                }
            });
        return actions;
    }, [isBulkJitPossible]);

    const bulkSnackbar = useMemo(
        () => (
            <FloatingSnackbar
                open={selectedGuids.length > 0}
                onClose={() => clearSelectedGuids()}
                selections={selectedGuids.length}>
                <div className="flex gap-2">
                    {snackbarActions.map((action, index) => (
                        <Button
                            key={index}
                            buttonStyle={ButtonStyles.Ghost}
                            text={action.label}
                            onClick={action.action}
                        />
                    ))}
                </div>
            </FloatingSnackbar>
        ),
        [selectedGuids.length, snackbarActions, clearSelectedGuids]
    );

    const bulkPolicyDrawer = useMemo(() => {
        if (!bulkAction) {
            return null;
        }
        const BulkPolicyComponent = bulkAction === BulkAction.JIT ? BulkJit : BulkFencing;
        return (
            <BulkPolicyComponent
                className="z-40"
                accountGuids={selectedGuids}
                open={bulkAction !== null}
                onClose={() => {
                    setBulkAction(null);
                    clearSelectedGuids();
                }}
                bulkAction={bulkAction}
                onSave={(message) => {
                    updatePrivilegedAccountsAfterPolicyChange(
                        selectedGuids,
                        bulkAction === BulkAction.JIT || null,
                        bulkAction === BulkAction.FENCING || null
                    );
                    showAlert(message, 'success', {
                        title: 'Success',
                        icon: Icons.CircleCheck
                    });
                    fetchStatsData();
                    updatePoliciesStatus();
                    clearSelectedGuids();
                    setShowTopBanner(false);
                    setBulkAction(null);
                }}
                onError={(error) =>
                    showAlert(error, 'error', {
                        title: 'Error',
                        icon: Icons.Warning
                    })
                }
                shouldVerifyAction={isBulkOverrides}
            />
        );
    }, [bulkAction, selectedGuids, isBulkOverrides]);

    const updatePrivilegedAccountsAfterPolicyChange = useCallback(
        (changeAccountsGuids, jitEnabled = null, fencingEnabled = null) => {
            const guidsToUpdate = [];
            setPrivilegedAccounts((privilegedAccounts) => {
                const newAccounts = [...privilegedAccounts];
                changeAccountsGuids.forEach((accountGuid) => {
                    const userToChange = newAccounts.find((user) => user.guid === accountGuid);

                    if (jitEnabled !== null && userToChange.jitEnabled !== jitEnabled) {
                        userToChange.status = userToChange.jitEnabled
                            ? PRIVILEGED_ACCOUNTS_STATUS.Enabling
                            : PRIVILEGED_ACCOUNTS_STATUS.Disabling;
                        guidsToUpdate.push(accountGuid);
                        userToChange.jitEnabled = jitEnabled;
                    }
                    if (fencingEnabled !== null && userToChange.fencingEnabled !== fencingEnabled)
                        userToChange.fencingEnabled = fencingEnabled;
                });
                if (guidsToUpdate.length > 0) {
                    setGuidsOfJitPolicyInActivationProcess((prev) => [...prev, ...guidsToUpdate]);
                }
                return newAccounts;
            });
        },
        []
    );

    return (
        <div className="w-screen h-screen bg-sf-white">
            <div className={`z-30 content`} id="wrapper">
                <TopBanner
                    icon={Icons.Warning}
                    title={'Changes cannot be saved due to an unexpected error. Try again later.'}
                    className="z-40"
                    show={showTopBanner}
                    onClose={() => setShowTopBanner(false)}
                />
            </div>
            {bulkPolicyDrawer}
            {openedAccountProtectionGuid && (
                <PaPolicies
                    className="z-40"
                    isEditor={isEditor}
                    open={!!openedAccountProtectionGuid}
                    onClose={() => {
                        setOpenedAccountProtectionGuid(null);
                        setHighlightedUser('');
                    }}
                    account={privilegedAccounts.find((account) => account.guid === openedAccountProtectionGuid)}
                    onSave={(account, jitEnabled, fencingEnabled) => {
                        buildPolicyAlertPopup(account, jitEnabled, fencingEnabled);
                        updatePrivilegedAccountsAfterPolicyChange([account.guid], jitEnabled, fencingEnabled);
                        fetchStatsData();
                        updatePoliciesStatus();
                    }}
                    onError={(error) => {
                        showAlert(error, 'error', {
                            title: 'Error',
                            icon: Icons.Warning
                        });
                        setShowTopBanner(true);
                    }}></PaPolicies>
            )}
            {statistics && !showLoader ? (
                <div className={`pt-[60px] overflow-auto h-full bg-sf-white ml-[60px]`}>
                    <div className="h-full  flex flex-col p-6 gap-6">
                        <PrivilegedAccountsInsights
                            statistics={statistics}
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                            isEditor={isEditor}
                        />
                        <div className="flex flex-col gap-4">
                            <PrivilegedAccountsFilters
                                selectedFilters={selectedFilters}
                                setSelectedFilters={setSelectedFilters}
                                onSearchChange={onSearchChange}
                            />
                            <div className="h-12 flex justify-between">
                                <div>
                                    <Tabs
                                        disabledTabs={[2]}
                                        selectedTab={selectedTab}
                                        onClick={(tabId) => setSelectedTab(tabId)}
                                        tabs={tabs}
                                        tabClassName={'pr-[12px]'}
                                    />
                                </div>
                                {onPremNHIEnabled && (
                                    <div className="flex gap-1 items-center">
                                        <Checkbox onChange={onHideSaChange} checked={selectedFilters.hideSA} />{' '}
                                        <div className="text-sf">Hide Service Accounts</div>
                                    </div>
                                )}
                            </div>
                            <Table
                                columns={tableColumns}
                                rows={tableData}
                                getRowCheckboxProps={(pa) =>
                                    pa.is_silverfort_account && {
                                        disabled: true,
                                        tooltipTitle: 'Bulk action not supported for Silverfort service account'
                                    }
                                }
                                selectable={isEditor}
                                selected={selectedGuids}
                                onSelect={onSelectPas}
                                onSortChange={(column, dir) => {
                                    setSortSettings({ orderBy: column, sortDirection: dir });
                                }}
                                menuItems={[
                                    {
                                        title: 'Protection settings',
                                        onClick: handleProtectionSettings,
                                        IconLeft: Icons.ShieldCheck
                                    }
                                ]}
                                getRowMenuProps={(row) => {
                                    return {
                                        disabled: row.is_silverfort_account,
                                        tooltipTitle: row.is_silverfort_account
                                            ? 'Silverfort service account protection settings cannot be changed'
                                            : ''
                                    };
                                }}
                                expandableRowsMode={true}
                                onScrollEnd={() => {
                                    !lastPageReceived && setAccountsPageNumber(accountsPageNumber + 1);
                                }}
                                expandedDataKey={'expandedTable'}
                                defaultSortColumn={sortSettings.orderBy}
                                defaultSortDirection={sortSettings.sortDirection}
                                loading={showPageLoader}
                                onExpand={handleOnExpand}
                                keyExtractor={(item) => item.guid}
                                tableContainerClassName={'w-full h-[calc(100vh-364px)]'}
                                EmptyStateProps={{
                                    className: 'w-full h-[250px] border border-sf-gray-100 rounded-lg py-12',
                                    title: 'No Results',
                                    description: 'Try refining your search',
                                    icon: EmptyStateIcons.NoResults
                                }}
                                highlighted={[highlightedUser]}
                            />
                            {selectedGuids.length > 0 && !bulkAction && !openedAccountProtectionGuid && bulkSnackbar}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="silverfort-loader">
                    <div className="logo"></div>
                </div>
            )}
            <Alert />
        </div>
    );
};

export default PrivilegedUsers;
